<template>
  <div>
    <div class="px-2 mt-3 mb-0">
      <v-text-field
        v-model="search"
        placeholder="Zoeken..."
        solo
        clearable
        @click:clear="clearSearch"
      >
        <template #append>
          <CounterBadgeIcon
            :counter="selected_options"
            icon="mdi-calendar-check-outline"
            @click="openFilterWindow"
          />
        </template>
      </v-text-field>
      <div class="d-flex justify-end mb-2">
        <v-btn
          color="primary"
          prepend-icon="mdi-plus"
          size="small"
          @click="createDeadline"
        >
          Nieuwe taak
        </v-btn>
      </div>
    </div>

    <DeadlineFilterDialog
      v-model="q"
      v-model:show="show"
      @search="applyFilters"
      @clear_search="clearSearchString"
      @selected_options="updateSelectedOptions"
    />

    <v-card class="mb-4">
      <v-card-title class="d-flex align-center">
        <v-icon start color="green-darken-1" class="mr-2"
          >mdi-calendar-check</v-icon
        >
        Openstaande taken
      </v-card-title>
      <v-card-text class="pa-0">
        <DeadlineList :deadlines="openSorted" :show_account="true" />
      </v-card-text>
    </v-card>

    <v-card>
      <v-card-title class="d-flex align-center justify-space-between">
        <div class="d-flex align-center">
          <v-icon start color="blue-darken-1" class="mr-2">mdi-history</v-icon>
          Recent gesloten (laatste {{ days }} dagen)
        </div>
        <v-select
          v-model="days"
          :items="dayOptions"
          item-title="label"
          item-value="value"
          label="Periode"
          density="compact"
          style="max-width: 160px"
          variant="outlined"
        />
      </v-card-title>
      <v-card-text class="pa-0">
        <DeadlineList
          :deadlines="recentClosedSorted"
          :show_account="true"
          :include_closed="true"
        />
      </v-card-text>
    </v-card>
  </div>
</template>

<script>
import DeadlineList from "./DeadlineList.vue";
import { useDeadlinesStoreRefactor } from "../../stores/deadlinesStoreRefactor";
import DeadlineFilterDialog from "./DeadlineFilterDialog.vue";
import CounterBadgeIcon from "../ui/CounterBadgeIcon.vue";
import { ref, computed, onMounted, watch } from "vue";
import { useFormsStore } from "@/stores/formsStore";
import moment from "moment";

export default {
  name: "DeadlineOverview",
  components: {
    DeadlineList,
    DeadlineFilterDialog,
    CounterBadgeIcon,
  },
  setup() {
    const deadlinesStore = useDeadlinesStoreRefactor();
    const formsStore = useFormsStore();

    const loading = ref(false);
    const search = ref("");
    const show = ref(false);
    const selected_options = ref(0);
    const searchTimeout = ref(null);
    // Default to 7 days
    const days = ref(7);
    const dayOptions = [
      { label: "7 dagen", value: 7 },
      { label: "30 dagen", value: 30 },
      { label: "60 dagen", value: 60 },
      { label: "90 dagen", value: 90 },
      { label: "180 dagen", value: 180 },
    ];

    const q = ref({
      user_id_eq: "",
      owner_id_eq: "",
      account_id_not_null: "",
      description_i_cont_all: "",
      deadline_historical: "",
    });

    // Watch for search changes with debounce (client-side only)
    watch(search, (newSearch) => {
      if (searchTimeout.value) clearTimeout(searchTimeout.value);
      searchTimeout.value = setTimeout(() => applyFilters(), 250);
    });

    // Refetch when period changes
    watch(days, async () => {
      await refreshData();
    });

    // Base list from store
    const all = computed(() => deadlinesStore.deadlines || []);

    // Client-side filter logic
    const applyTextSearch = (items) => {
      const s = (search.value || "").trim().toLowerCase();
      if (!s) return items;
      return items.filter((d) => {
        const str = (v) => (v == null ? "" : String(v)).toLowerCase();
        // Build a searchable blob
        const blob = [
          d.id,
          d.description,
          d.account_name,
          d.user_name,
          d.owner_name,
          d.deadline,
        ]
          .map(str)
          .join(" ");
        return blob.includes(s);
      });
    };

    const applyQFilters = (items) => {
      let filtered = items;
      if (q.value.user_id_eq) {
        filtered = filtered.filter(
          (d) => String(d.user_id) === String(q.value.user_id_eq),
        );
      }
      if (q.value.owner_id_eq) {
        filtered = filtered.filter(
          (d) => String(d.owner_id) === String(q.value.owner_id_eq),
        );
      }
      if (q.value.account_id_not_null === "true") {
        filtered = filtered.filter((d) => d.account_id != null);
      } else if (q.value.account_id_not_null === "false") {
        filtered = filtered.filter((d) => d.account_id == null);
      }
      if (q.value.deadline_historical === "true") {
        // Only overdue open items
        filtered = filtered.filter(
          (d) => !d.completed_at && new Date(d.deadline) < new Date(),
        );
      } else if (q.value.deadline_historical === "false") {
        // Only not overdue (open due today/future) or closed items
        filtered = filtered.filter(
          (d) => d.completed_at || new Date(d.deadline) >= new Date(),
        );
      }
      return filtered;
    };

    const filteredAll = computed(() => {
      // Apply q filters first, then text search
      const afterQ = applyQFilters(all.value);
      return applyTextSearch(afterQ);
    });

    const openSorted = computed(() => {
      return filteredAll.value
        .filter((d) => !d.completed_at)
        .sort((a, b) => new Date(a.deadline) - new Date(b.deadline));
    });

    const recentClosedSorted = computed(() => {
      const cutoff = new Date();
      cutoff.setDate(cutoff.getDate() - Number(days.value || 7));
      return filteredAll.value
        .filter((d) => d.completed_at && new Date(d.completed_at) >= cutoff)
        .sort((a, b) => new Date(a.deadline) - new Date(b.deadline));
    });

    // Methods
    const refreshData = async () => {
      loading.value = true;
      try {
        await deadlinesStore.fetchOpenAndRecentClosed(days.value);
      } catch (error) {
        console.error("Error refreshing deadlines:", error);
      } finally {
        loading.value = false;
      }
    };

    const clearSearch = () => {
      search.value = "";
    };

    const applyFilters = () => {
      // No-op: computed props will react to changes in search/q
    };

    const updateSelectedOptions = (count) => {
      selected_options.value = count;
    };

    const openFilterWindow = () => {
      show.value = true;
    };

    const closeFilterDialog = () => {
      show.value = false;
    };

    const clearSearchString = () => {
      search.value = "";
      q.value = {
        user_id_eq: "",
        owner_id_eq: "",
        account_id_not_null: "",
        description_i_cont_all: "",
        deadline_historical: "",
      };
    };

    function createDeadline() {
      formsStore.edit({
        type: "deadline",
        item: {
          deadline: moment().add(1, "day").format("YYYY-MM-DD"),
          user_id: null,
          account_id: null,
        },
        options: {},
      });
    }

    onMounted(() => {
      refreshData();
    });

    return {
      loading,
      search,
      show,
      selected_options,
      q,
      days,
      dayOptions,
      openSorted,
      recentClosedSorted,
      refreshData,
      clearSearch,
      applyFilters,
      updateSelectedOptions,
      openFilterWindow,
      closeFilterDialog,
      clearSearchString,
      createDeadline,
    };
  },
};
</script>

<style scoped>
/* Add any custom styling here */
</style>
